import { useMutation } from "@tanstack/react-query";
import { FormControl, Text, VStack } from "native-base";
import React, { useState } from "react";

import FormButton from "@/components/atoms/FormButton";
import TextInput from "@/components/atoms/TextInput";
import Modal from "@/components/molecules/Modal";
import { Voucher, VoucherTypesEnum } from "@/interfaces/domain/Voucher";
import {
  ValidateVoucherPayload,
  ValidateVoucherResult,
  validateVoucher,
} from "@/services/api/voucher";
import { logger } from "@/services/logger";

import { ModalPropos, VoucherDatails } from "../types";

const VoucherModal = ({
  isOpen,
  currentValue,
  clientId,
  shipmentValue,
  storeId,
  subtotal,
  onClose,
  onSubmit,
  onRemove,
}: ModalPropos<VoucherDatails> & {
  storeId: string;
  clientId: string;
  subtotal: number;
  shipmentValue: number;
  onRemove: () => void;
}) => {
  const [code, setCode] = useState(currentValue?.voucher?.codigo || "");
  const [error, setError] = useState("");

  const isResponseWithError = (data: any): data is { error: string } => !!data?.error;

  const getRestrictedDays = (data: Voucher) => {
    return data.tipo === VoucherTypesEnum.CONDOMINIUM
      ? data.condominio?.dias_promocao?.map((day) => ({
          day: day.dia_promocao,
          restrictedHours:
            data.condominio?.tipo_horario === "fixo"
              ? day.horarios_dia.map((h) => Number(h.split(":")[0]))
              : undefined,
        }))
      : null;
  };

  const { mutate, isLoading } = useMutation<ValidateVoucherResult, unknown, ValidateVoucherPayload>(
    {
      mutationFn: validateVoucher,
      onSuccess: (data) => {
        if (isResponseWithError(data)) {
          return setError(data.error);
        }
        onSubmit({
          voucher: data,
          initialShipmentValue: shipmentValue,
          initialSubtotal: subtotal,
          initialTotal: subtotal + shipmentValue,
          restrictedDays: getRestrictedDays(data),
        });
      },
      onError: (error) => {
        logger.error(error);
        setError("Não foi possível ativar o cupom");
      },
    }
  );

  const onSubmitVoucher = () => {
    mutate({
      clientId,
      code,
      orderAmount: subtotal,
      shipmentValue,
      storeId,
    });
  };

  const onRemoveVoucher = () => {
    setCode(null);
    onRemove();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      Header="Cupom de desconto"
      Body={
        <VStack>
          <Text fontSize="md" py="4">
            Digite o Cupom de desconto
          </Text>
          <FormControl isInvalid={!!error}>
            <TextInput
              value={code}
              onChangeText={(value) => setCode(value.toUpperCase())}
              placeholder="Cupom"
            />
            <FormControl.ErrorMessage>{error}</FormControl.ErrorMessage>
          </FormControl>
        </VStack>
      }
      Footer={
        <VStack w="full">
          {currentValue ? (
            <FormButton colorScheme="red" textColor="white.50" onPress={onRemoveVoucher}>
              Remover
            </FormButton>
          ) : (
            <FormButton isLoading={isLoading} mt="4" onPress={onSubmitVoucher}>
              Aplicar
            </FormButton>
          )}
          <FormButton
            colorScheme="white"
            textColor="primary.700"
            my="4"
            shadow="2"
            onPress={onClose}>
            Cancelar
          </FormButton>
        </VStack>
      }
    />
  );
};

export default VoucherModal;
